<!-- eslint-disable vue/no-template-shadow -->
<template>
  <div>
    <StateRenderer v-bind="state">
      <!-- Alerta quando não há brinquedos/produtos -->
      <b-alert
        v-if="state.data.length === 0"
        show
        variant="secondary"
        class="text-center"
      >
        <p>Nenhum brinquedo ou produto cadastrado</p>
        <b-button
          v-if="isAdmin"
          size="sm"
          variant="primary"
          :to="{ name: 'ToyRegister' }"
          class="mr-1"
        >
          <span class="fas fa-plus mr-1" /> Cadastrar Brinquedo
        </b-button>
        <b-button
          v-if="isAdmin"
          size="sm"
          variant="primary"
          :to="{ name: 'ProductRegister' }"
        >
          <span class="fas fa-plus mr-1" /> Cadastrar Produto
        </b-button>
      </b-alert>

      <div v-else>
        <!-- Banner de modo de seleção -->
        <b-alert
          v-if="selectionModeActive"
          show
          variant="info"
          class="text-center"
        >
          Modo de seleção ativado. Clique em um brinquedo disponível para
          iniciar um novo aluguel.
          <b-button size="sm" variant="secondary" @click="cancelSelectionMode">
            Cancelar
          </b-button>
        </b-alert>

        <!-- Filtros de busca e status -->
        <div class="d-flex gap-1 flex-column my-2">
          <b-input
            v-model="filter.search"
            type="search"
            size="lg"
            placeholder="Buscar brinquedo, cliente ou usuário"
          />
          <div class="d-flex flex-wrap gap-1">
            <b-button
              variant="success"
              class="w-100 flex-1"
              @click="setStatusFilter(FILTERS_IDS.AVAILABLE)"
            >
              <span class="fas fa-check" /> Disponíveis
            </b-button>
            <b-button
              variant="primary"
              class="w-100 flex-1"
              @click="setStatusFilter(FILTERS_IDS.RENT)"
            >
              <span class="fas fa-user" /> Alugados
            </b-button>
            <b-button
              variant="danger"
              class="w-100 flex-1"
              @click="setStatusFilter(FILTERS_IDS.DELAYED)"
            >
              <span class="fas fa-clock" /> Atrasados
            </b-button>
            <b-button
              variant="secondary"
              class="w-100 flex-1"
              @click="setStatusFilter(FILTERS_IDS.PAUSED)"
            >
              <span class="fas fa-pause" /> Pausados
            </b-button>
            <b-button
              variant="info"
              class="w-100 flex-1"
              @click="setStatusFilter(FILTERS_IDS.ALL)"
            >
              <span class="fas fa-circle" /> Todos
            </b-button>
          </div>
        </div>

        <!-- Renderização por quantidade de lojas -->
        <div v-if="hasAnyToys || hasAnyProducts">
          <component
            :is="isSingleStore ? 'SingleStoreBoard' : 'MultiStoreBoard'"
            :single-store-id="singleStoreId"
            :filtered-stores="filteredStores"
            :toys-by-store="toysByStore"
            :products-by-store="productsByStore"
            :selection-mode-active="selectionModeActive"
            :checkouts-by-store="checkoutsByStore"
            :has-open-checkout="hasOpenCheckout"
            :can-show-store-items="canShowStoreItems"
            :get-store-name="getStoreName"
            :is-admin="isAdmin"
            :should-show-checkout-alert="shouldShowCheckoutAlert"
            :get-checkout-alert-message="getCheckoutAlertMessage"
            @update-store-id="setSelectedStoreId"
            @trigger-action="onTriggerAction"
            @activate-selection-mode="activateSelectionMode"
            @new-rental="handleNewRental"
            @reload="onReload"
            @emitPaymentAmountsForMultiFormatPayments="
              onHandlePaymentAmountsUpdate
            "
          />
        </div>

        <!-- Quando NADA casa com o filtro/pesquisa -->
        <b-alert v-else show variant="secondary" class="text-center">
          Nenhum brinquedo ou produto encontrado para os filtros atuais.
          <b-button
            size="sm"
            variant="outline-primary"
            class="ml-2"
            @click="clearFilters"
          >
            Limpar filtros
          </b-button>
        </b-alert>

        <!-- Modal de aluguel -->
        <ToyRentModal
          v-if="showToyRentModal"
          :key="
            toy.id +
            '-' +
            (data && data.prefilledClient
              ? data.prefilledClient.id
              : 'default') +
            '-' +
            modalKey
          "
          v-model="isModalOpen"
          :toy="toy"
          :action="action"
          :store-id="selectedStoreId"
          :validations="validations"
          :initial-state="initialState"
          :rent-amount="currentRentAmount"
          :products-amount="currentProductsAmount"
          :prefilled-client="
            data && data.prefilledClient ? data.prefilledClient : null
          "
          :prefilled-period="
            data && data.prefilledPeriod ? data.prefilledPeriod : null
          "
          :store-has-commissions="selectedStoreHasCommissions"
          @ok="onOk"
        />
      </div>
    </StateRenderer>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import {
  FILTERS_IDS,
  HOME_ITEM_TYPES,
  PAYMENT_METHODS,
  PRODUCT_ACTIONS,
  RENT_ACTIONS,
  RENT_ACTIONS_INITIAL_STATES
} from '@/constants'
import { required } from 'vuelidate/lib/validators'
import { calculateRentAmount, filterNulledPayments } from '@/helpers/rents'
import { parseProducts } from '@/helpers/products'
import { filterItems } from '@/helpers/home'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import StateRenderer from '@/components/StateRenderer'
import ToyRentListItem from './ToyRentListItem'
import ProductListItem from './ProductListItem'
import ToyRentModal from './ToyRentModal'
import SingleStoreBoard from './partials/storePresentation/SingleStoreBoard.vue'
import MultiStoreBoard from './partials/storePresentation/MultiStoreBoard.vue'
import { formatCurrency } from '@/helpers/numbers'
export default {
  name: 'HomeList',
  components: {
    StateRenderer,
    ToyRentListItem,
    ProductListItem,
    ToyRentModal,
    SingleStoreBoard,
    MultiStoreBoard
  },
  mixins: [
    withAsyncAction({
      key: 'products',
      initialState: [],
      fetcher: {
        methodName: 'fetchProducts',
        handler: services.products.fetchProducts,
        stateUpdater: ({ currentState }) => parseProducts(currentState)
      }
    })
  ],
  data() {
    return {
      hideLoading: false,
      isModalOpen: false,
      action: '',
      initialState: {},
      toy: {},
      validations: {
        reason: { required }
      },
      backgroundFetching: false,
      filter: {
        search: '',
        status: FILTERS_IDS.ACTIVE
      },
      selectionModeActive: false,
      prefilledClient: null,
      prefilledPeriod: null,
      prefilledStoreId: null,
      toysUpdateIntervals: {},
      fetchDataInterval: null,
      currentRentAmount: 0,
      currentProductsAmount: 0,
      data: {},
      modalKey: 0,
      storesData: {},
      selectedStoreId: null
    }
  },
  computed: {
    ...mapState('multiStoresSse', ['toyUpdates']),
    ...mapGetters('store', ['isAdmin', 'isShopping']),
    ...mapGetters({
      store: ['store/currentStoreId']
    }),
    ...mapState('periods', ['periods']),
    ...mapState('checkout', ['currentCheckout']),
    ...mapState('store', ['currentStore']),
    ...mapGetters('periods', ['periodsSortedByTime']),
    filteredStores() {
      // Une stores que têm brinquedos filtrados OU produtos (pós-busca)
      const stores = new Set()

      Object.keys(this.toysByStore).forEach((storeId) => {
        if (this.toysByStore[storeId]?.length) stores.add(storeId)
      })
      Object.keys(this.productsByStore).forEach((storeId) => {
        if (this.productsByStore[storeId]?.length) stores.add(storeId)
      })

      return Array.from(stores).map((storeId) => ({
        storeId,
        storeData: this.storesData[storeId] || { name: `Shopping ${storeId}` }
      }))
    },
    state() {
      const loading = !this.hideLoading && this.products.loading
      const error = this.products.error
      const data = [
        ...Object.values(this.toyUpdates).flat(),
        ...this.products.data
      ]
      return { loading, error, data }
    },
    // Agrupa os brinquedos por shopping
    toysByStore() {
      const grouped = {}
      Object.entries(this.toyUpdates).forEach(([storeId, storeData]) => {
        const toys = storeData.toys
        if (!Array.isArray(toys)) return
        const typedToys = toys.map((toy) => ({
          ...toy,
          rent: (toy.rents && toy.rents[0]) || null,
          type: HOME_ITEM_TYPES.TOY
        }))
        const filtered = filterItems({ items: typedToys, filter: this.filter })
        if (filtered.length > 0) grouped[storeId] = filtered
      })
      return grouped
    },
    filteredToys() {
      return Object.values(this.toysByStore).flat()
    },
    productsCount() {
      return Object.values(this.productsByStore).reduce(
        (acc, arr) => acc + arr.length,
        0
      )
    },
    hasAnyToys() {
      return this.filteredToys.length > 0
    },
    hasAnyProducts() {
      return this.productsCount > 0
    },
    productsByStore() {
      const grouped = {}
      const term = (this.filter.search || '').toLowerCase().trim()

      this.products.data.forEach((product) => {
        // filtra apenas pelo termo (nome / sku / barcode); ignora status
        if (term) {
          const haystack = [
            product.name,
            product.sku,
            product.barcode,
            product.description
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

          if (!haystack.includes(term)) return
        }

        const storeId = product.storeId
        if (!grouped[storeId]) grouped[storeId] = []
        grouped[storeId].push({ ...product, type: HOME_ITEM_TYPES.PRODUCT })
      })

      return grouped
    },
    filteredProducts() {
      const term = (this.filter.search || '').toLowerCase().trim()
      return this.products.data
        .filter((p) => {
          if (!term) return true
          const haystack = [p.name, p.sku, p.barcode, p.description]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return haystack.includes(term)
        })
        .map((p) => ({ ...p, type: HOME_ITEM_TYPES.PRODUCT }))
    },
    canStartRent() {
      const { currentCheckout, periods } = this
      return currentCheckout && periods.data.length > 0
    },
    showToyRentModal() {
      const { action, toy } = this
      return action && toy
    },
    checkoutsByStore() {
      const checkouts = {}
      Object.entries(this.toyUpdates).forEach(([storeId, storeData]) => {
        checkouts[storeId] = storeData.checkout || null
      })
      return checkouts
    },
    isSingleStore() {
      return this.filteredStores.length === 1
    },
    singleStoreId() {
      return this.isSingleStore ? this.filteredStores[0].storeId : null
    },
    selectedStoreHasCommissions() {
      const storeId =
        this.selectedStoreId != null
          ? this.selectedStoreId
          : this.singleStoreId != null
          ? this.singleStoreId
          : this.currentStore?.id || this.store

      if (!storeId) return null

      const storeData = this.storesData[storeId]
      if (storeData && typeof storeData.hasCommissions !== 'undefined') {
        return Boolean(storeData.hasCommissions)
      }

      if (
        this.currentStore?.id === storeId &&
        typeof this.currentStore.hasCommissions !== 'undefined'
      ) {
        return Boolean(this.currentStore.hasCommissions)
      }

      // Sem dado carregado para a loja selecionada, n‹  for‡amos outro valor.
      return null
    }
  },
  watch: {
    toyUpdates: {
      handler(newVal) {
        Object.keys(newVal).forEach((storeId) => {
          this.$set(this.toyUpdates, storeId, newVal[storeId])
          this.fetchStoreData(storeId)
        })
      },
      deep: true
    },
    toysByStore: {
      handler(newVal) {
        Object.keys(newVal).forEach((storeId) => {
          this.fetchStoreData(storeId)
        })
      },
      immediate: true
    },
    productsByStore: {
      handler(newVal) {
        Object.keys(newVal).forEach((storeId) => {
          this.fetchStoreData(storeId)
        })
      },
      immediate: true
    }
  },
  async created() {
    if (this.isShopping) {
      this.$router.push({ name: 'ReportsIndex' })
      return
    }
    this.FILTERS_IDS = FILTERS_IDS
    this.fetchData()
    this.fetchDataInterval = setInterval(() => {
      this.fetchData()
    }, 120000)
  },
  beforeDestroy() {
    if (this.fetchDataInterval) {
      clearInterval(this.fetchDataInterval)
    }
  },
  methods: {
    clearFilters() {
      this.filter.search = ''
      this.filter.status = this.FILTERS_IDS.ALL
      this.filter.delayed = false
    },
    setSelectedStoreId(storeId) {
      this.selectedStoreId = storeId
      if (storeId && !this.storesData[storeId]) {
        this.fetchStoreData(storeId)
      }
    },
    shouldShowCheckoutAlert(storeId) {
      const checkout = this.checkoutsByStore[storeId]
      return (
        (checkout && checkout.closedAt !== null) ||
        (checkout && !checkout.closedAt && !this.isCreatedToday(checkout))
      )
    },
    getCheckoutAlertMessage(storeId) {
      const checkout = this.checkoutsByStore[storeId]
      if (checkout && checkout.closedAt !== null) {
        return 'Para iniciar um novo aluguel ou vender um produto é preciso abrir um caixa.'
      }
      return 'Para iniciar um novo aluguel ou vender um produto é preciso abrir o caixa do dia.'
    },
    isCreatedToday(checkout) {
      if (!checkout || !checkout.createdAt) return false
      const today = new Date()
      const createdDate = new Date(checkout.createdAt)
      return (
        today.getDate() === createdDate.getDate() &&
        today.getMonth() === createdDate.getMonth() &&
        today.getFullYear() === createdDate.getFullYear()
      )
    },
    canShowStoreItems(storeId) {
      const checkout = this.checkoutsByStore[storeId]
      return checkout && !checkout.closedAt && this.isCreatedToday(checkout)
    },
    hasOpenCheckout(storeId) {
      const checkout = this.checkoutsByStore[storeId]
      return checkout && !checkout.closedAt && this.isCreatedToday(checkout)
    },
    async fetchStoreData(storeId) {
      if (!this.storesData[storeId]) {
        try {
          const store = await services.stores.fetchStoreById(storeId)
          this.$set(this.storesData, storeId, store)
        } catch (error) {
          console.error('Erro ao buscar dados do shopping:', error)
          this.$set(this.storesData, storeId, { name: `Shopping ${storeId}` })
        }
      }
    },
    getStoreName(storeId) {
      if (!this.storesData[storeId]) {
        this.fetchStoreData(storeId)
        return `Shopping ${storeId}`
      }
      return this.storesData[storeId].name || `Shopping ${storeId}`
    },
    onHandlePaymentAmountsUpdate({ rentAmount, productsAmount }) {
      if (
        this.currentRentAmount !== rentAmount ||
        this.currentProductsAmount !== productsAmount
      ) {
        this.currentRentAmount = rentAmount
        this.currentProductsAmount = productsAmount
      }
    },
    ...mapActions('periods', ['fetchPeriods']),
    async fetchRentStatus(toyId) {
      const rentData = await services.toys.fetchToyById(toyId)
      if (rentData.rents && rentData.rents.length > 0) {
        return rentData.rents[0].status
      }
      return null
    },
    async fetchData() {
      this.hideLoading = true
      const { store: storeId } = this
      await Promise.all([
        this.fetchPeriods({ storeId }),
        this.fetchProducts({ storeId })
      ])
      this.hideLoading = false
    },
    isToy(item) {
      return item.type === HOME_ITEM_TYPES.TOY
    },
    isProduct(item) {
      return item.type === HOME_ITEM_TYPES.PRODUCT
    },
    setStatusFilter(status) {
      this.filter.status = status
      this.filter.delayed = false
    },
    ...mapActions('periods', ['fetchPeriods']),
    async onTriggerAction({ action, toy, product, data }) {
      this.modalKey++
      switch (action) {
        case RENT_ACTIONS.START:
        case RENT_ACTIONS.CANCEL:
        case RENT_ACTIONS.TRANSFER:
        case RENT_ACTIONS.ADD_EXTRA_TIME:
        case RENT_ACTIONS.CHANGE_PERIOD:
          this.isModalOpen = true
          this.initialState = RENT_ACTIONS_INITIAL_STATES[action]
          this.$nextTick(() => {
            this.toy = toy
            this.action = action
            this.data = data
          })
          break
        case RENT_ACTIONS.REOPEN_LAST_RENT:
          return this.reopenLastRent(toy)
        case RENT_ACTIONS.CLOSE:
          return this.closeRent(toy, data)
        case RENT_ACTIONS.PAUSE:
          return this.pauseRent(toy)
        case RENT_ACTIONS.RESUME:
          return this.resumeRent(toy)
        case RENT_ACTIONS.ADD_PRODUCT:
          return this.addProduct(toy, data)
        case RENT_ACTIONS.REMOVE_PRODUCT:
          return this.removeProduct(toy, data)
        case PRODUCT_ACTIONS.BUY:
          return this.buyProduct(product, data)
        case RENT_ACTIONS.RESCUE:
          await services.rents.rescueRent({ id: toy.rent.id })
          this.$root.$toast.success('Aluguel resgatado com sucesso')
          this.closeModal()
          break
        default:
          return
      }
    },
    onOk({ toy, form }) {
      switch (this.action) {
        case RENT_ACTIONS.START:
          return this.startRent(toy, form)
        case RENT_ACTIONS.PAUSE:
          return this.pauseRent(toy, form)
        case RENT_ACTIONS.CANCEL:
          return this.cancelRent(toy, form)
        case RENT_ACTIONS.TRANSFER:
          return this.transferRent(toy, form)
        case RENT_ACTIONS.ADD_EXTRA_TIME:
          return this.addExtraTime(toy, form)
        case RENT_ACTIONS.CHANGE_PERIOD:
          return this.changePeriod(toy, form)
        case RENT_ACTIONS.CLOSE:
          return this.closeRentMultiplePayment(toy, form)
        default:
          return
      }
    },
    async startRent(
      toy,
      {
        id,
        document,
        name,
        phone,
        email,
        updateCustomer,
        periodId,
        commissionUserStoreId // 👈 NOVO CAMPO vindo do form
      }
    ) {
      const toyId = toy.id

      await services.rents.startRent({
        toyId,
        periodId,
        storeId: toy.storeId,
        customer: updateCustomer
          ? { id, name, phone, email, document }
          : { id },
        commissionUserStoreId
      })

      this.$root.$toast.success('Aluguel iniciado com sucesso')
      this.closeModal()
    },
    async pauseRent(toy) {
      const {
        name,
        rent: { id }
      } = toy
      const result = await this.$bvModal.msgBoxConfirm(
        `Deseja realmente pausar o aluguel de ${name}?`
      )
      if (result) {
        await services.rents.pauseRent({ id })
        this.$root.$toast.success('Aluguel pausado com sucesso')
      }
    },
    async resumeRent(toy) {
      const {
        name,
        rent: { id }
      } = toy
      const message = `Deseja realmente retomar o aluguel de ${name}?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        await services.rents.resumeRent({ id })
        this.$root.$toast.success('Aluguel retomado com sucesso')
        this.closeModal()
      }
    },
    async closeRent(
      toy,
      { elapsedTime, hasProducts, paymentMethod, ignoreRentForPoint }
    ) {
      const { rent } = toy
      const { id } = rent
      if (paymentMethod) {
        const { currentStore } = this
        const { delayFeePerMinute = 1, toleranceMinutes = 2 } = currentStore
        const { rentAmount, productsAmount } = calculateRentAmount({
          rent,
          elapsedTime,
          hasProducts,
          delayFeePerMinute,
          toleranceMinutes
        })
        const amount = rentAmount + productsAmount
        this.closeRentSinglePayment({
          id,
          paymentMethod,
          amount,
          rent,
          ignoreRentForPoint,
          toy
        })
      } else {
        this.isModalOpen = true
        this.initialState = RENT_ACTIONS_INITIAL_STATES[RENT_ACTIONS.CLOSE]
        this.$nextTick(() => {
          this.toy = toy
          this.action = RENT_ACTIONS.CLOSE
        })
      }
    },
    async closeRentSinglePayment({
      id,
      paymentMethod,
      amount,
      ignoreRentForPoint,
      toy
    }) {
      const message = `Deseja realmente encerrar o aluguel do brinquedo com pagamento em ${PAYMENT_METHODS[paymentMethod].name}?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        const payments = [
          {
            type: paymentMethod,
            amount
          }
        ]
        const checkoutId = this.checkoutsByStore[toy.storeId]?.id
        await services.rents.closeRent({
          id,
          checkoutId,
          payments,
          ignoreRentForPoint,
          status: toy.rent.status
        })
        this.$root.$toast.success('Aluguel encerrado com sucesso')
        this.closeModal()
      }
    },
    async closeRentMultiplePayment(toy, { payments }) {
      const { id } = toy.rent
      const checkoutId = this.checkoutsByStore[toy.storeId]?.id
      const validPayments = filterNulledPayments(payments)
      await services.rents.closeRent({
        id,
        checkoutId,
        payments: validPayments,
        status: toy.rent.status
      })
      this.$root.$toast.success('Aluguel encerrado com sucesso')
      this.closeModal()
    },
    async buyProduct(product, data) {
      const quantity = Number(data.quantity || 1)
      const unit = Number(product.price || 0)
      const total = unit * quantity

      // Fluxo MULTI
      if (Array.isArray(data.payments) && data.payments.length > 0) {
        const breakdown = data.payments
          .map(
            (p) =>
              `${formatCurrency(Number(p.amount || 0))} - ${
                PAYMENT_METHODS[p.type]?.name || p.type
              }`
          )
          .join('\n')

        const msg = [
          `Confirmar saída de ${quantity} ${
            quantity === 1 ? 'unidade' : 'unidades'
          } do produto "${product.name}"`,
          `Pagamento em:\n${breakdown}`
        ].join('\n\n')

        const result = await this.$bvModal.msgBoxConfirm(msg)
        if (!result) return

        // soma deve bater com total
        const sum = data.payments.reduce(
          (acc, p) => acc + Number(p.amount || 0),
          0
        )
        if (Math.abs(sum - total) > 0.01) {
          this.$root.$toast.error(
            'A soma das formas de pagamento deve ser igual ao total.'
          )
          return
        }

        const checkoutId = this.currentCheckout?.id
        if (!checkoutId) {
          this.$root.$toast.error('Abra o caixa do dia para finalizar a venda.')
          return
        }

        const payload = data.payments.map((p) => ({
          type: p.type,
          amount: Number(p.amount),
          checkoutId,
          productId: product.id
        }))
        console.log(data)
        await services.payments.createPayment(payload)
        this.$root.$toast.success(
          `Compra do produto ${product.name} realizada com sucesso`
        )
        return
      }

      // Fluxo ÚNICO (como já era)
      const type = data.paymentMethod
      const message = `Confirmar ${quantity} ${
        quantity === 1 ? 'unidade' : 'unidades'
      } do produto "${product.name}" com pagamento em ${
        PAYMENT_METHODS[type].name
      }?`

      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        const productId = product.id
        const amount = unit * quantity
        const checkoutId = this.currentCheckout.id
        await services.payments.createPayment({
          type,
          amount,
          checkoutId,
          productId
        })
        this.$root.$toast.success(
          `Compra do produto ${product.name} realizada com sucesso`
        )
      }
    },
    async reopenLastRent(toy) {
      const { name, lastRentId: id } = toy
      const message = `Deseja realmente reiniciar o último aluguel do brinquedo ${name}?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        await services.rents.reopenLastRent({ id })
        this.$root.$toast.success('Aluguel reiniciado com sucesso')
        this.closeModal()
      }
    },
    async cancelRent(toy, { reason }) {
      const {
        rent: { id }
      } = toy
      await services.rents.cancelRent({ id, reason })
      this.$root.$toast.success('Aluguel cancelado com sucesso')
      this.closeModal()
    },
    async transferRent(toy, form) {
      const { rent } = toy
      if (rent.endedAt || rent.canceledAt) {
        return
      }
      const message = `Deseja realmente transferir o aluguel do brinquedo ${toy.name} para o brinquedo ${form.toy.label}?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        const toyId = form.toy.code
        await services.rents.updateRent({ ...rent, toyId })
        this.$root.$toast.success('Aluguel transferido com sucesso')
        this.closeModal()
      }
    },
    async addExtraTime(toy, { time, reason }) {
      const {
        rent: { id }
      } = toy
      await services.rents.addExtraTime({ id, time, reason })
      this.$root.$toast.success('Tempo extra adicionado com sucesso')
      this.closeModal()
      this.checkPeriod(toy, time)
    },
    async checkPeriod(toy, extraTime) {
      const { rent } = toy
      if (rent.endedAt || rent.canceledAt) {
        return
      }
      const parsedExtraTime = isNaN(extraTime) ? 0 : Number(extraTime)
      const { elapsedTime, rentAmount } = this.data
      const prevPeriod = this.periodsSortedByTime.find(
        ({ id, time, price }) => {
          return (
            id !== rent.period.id &&
            elapsedTime - parsedExtraTime <= time &&
            price < rentAmount
          )
        }
      )
      if (prevPeriod) {
        await services.rents.updateRent({ ...rent, periodId: prevPeriod })
      }
      this.toy = null
    },
    async changePeriod(toy, { periodId }) {
      const { rent } = toy
      if (rent.endedAt || rent.canceledAt) {
        return
      }
      await services.rents.updateRent({ ...rent, periodId })
      this.$root.$toast.success('Período alterado com sucesso')
      this.closeModal()
      this.toy = null
    },
    async addProduct(toy, product) {
      const quantity = 1
      const message = `Deseja realmente incluir ${quantity} unidade(s) do produto ${product.name} a este aluguel?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        const {
          rent: { id }
        } = toy
        const productId = product.id
        await services.rents.addProduct({ id, productId, quantity })
        this.$root.$toast.success(`${product.name} incluído com sucesso`)
      }
    },
    async removeProduct(toy, product) {
      const message = `Deseja realmente remover o produto: ${product.name} deste aluguel?`
      const result = await this.$bvModal.msgBoxConfirm(message)
      if (result) {
        const {
          rent: { id }
        } = toy
        const productId = product.id
        await services.rents.removeProduct({ id, productId })
        this.$root.$toast.success(`${product.name} removido com sucesso`)
      }
    },
    async onReload() {
      this.backgroundFetching = true
      this.backgroundFetching = false
    },
    closeModal() {
      this.toy = null
      this.isModalOpen = false
      this.action = ''
    },
    activateSelectionMode(toy) {
      this.setSelectedStoreId(toy.storeId)
      this.selectionModeActive = true
      this.prefilledClient = toy.rent?.customer || null
      this.prefilledPeriod = toy.rent?.period || null
      this.prefilledStoreId = toy.storeId
      this.$root.$toast.warning(
        'Modo de seleção ativado. Agora, clique em um brinquedo disponível para iniciar um novo aluguel.'
      )
    },
    handleNewRental(toy) {
      if (toy.storeId !== this.prefilledStoreId) {
        this.$root.$toast.error(
          'Não é possível duplicar aluguel entre lojas diferentes.'
        )
        this.cancelSelectionMode()
        return
      }

      this.setSelectedStoreId(toy.storeId)
      this.onTriggerAction({
        action: RENT_ACTIONS.START,
        toy,
        data: {
          prefilledClient: this.prefilledClient,
          prefilledPeriod: this.prefilledPeriod
        }
      })
      this.selectionModeActive = false
      this.prefilledClient = null
    },
    cancelSelectionMode() {
      this.selectionModeActive = false
      this.$root.$toast.success('Modo de seleção cancelado.')
    }
  }
}
</script>
<style>
.toys-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

/* Bootstrap 4 breakpoints típicos (ajuste à vontade) */
@media (min-width: 350px) {
  /* ≥576px → 2 colunas */
  .toys-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 580px) {
  /* ≥768px → 3 colunas */
  .toys-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 992px) {
  /* ≥992px → 4 colunas */
  .toys-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
@media (min-width: 1200px) {
  /* ≥1200px → 5 colunas (se quiser) */
  .toys-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
